import type { PlatformBridge, PlatformType, PlatformUser, ShareParams, ThemeColors } from './types.ts';

/**
 * Типы MAX WebApp SDK.
 * Документация: https://dev.max.ru/docs/webapps/bridge
 * SDK подключается через <script src="https://st.max.ru/js/max-web-app.js">
 * и доступен как window.WebApp
 */
interface MaxWebApp {
  initData: string;
  initDataUnsafe: {
    query_id: string;
    auth_date: number;
    hash: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      username: string;
      language_code: string;
      photo_url: string;
    };
  };
  platform: 'ios' | 'android' | 'desktop' | 'web';
  version: string;
  requestContact(): Promise<{ phone: string; authDate: string; hash: string }>;
  BackButton: {
    show(): void;
    hide(): void;
    isVisible: boolean;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): Promise<{ status: string }>;
  };
  // ─── Расширение (итерация 8) ─────────────────────────────────────────────
  openLink(url: string): void;
  openMaxLink(url: string): void;
  share(params: ShareParams): Promise<{ status: 'shared' | 'cancelled' }>;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
}

declare global {
  interface Window {
    WebApp?: MaxWebApp;
  }
}

const DEFAULT_THEME: ThemeColors = {
  bgColor: '#ffffff',
  textColor: '#1a1a1a',
  hintColor: '#888888',
  linkColor: '#2563eb',
  buttonColor: '#16a34a',
  buttonTextColor: '#ffffff',
  secondaryBgColor: '#f3f4f6',
};

export class MaxBridge implements PlatformBridge {
  private sdk: MaxWebApp;

  constructor() {
    if (!window.WebApp) {
      throw new Error('MAX WebApp SDK not available');
    }
    this.sdk = window.WebApp;
  }

  getPlatform(): PlatformType {
    return 'max';
  }

  getUser(): PlatformUser {
    const u = this.sdk.initDataUnsafe?.user;
    if (!u) {
      return { id: 0, firstName: '', lastName: '', username: null, phone: null };
    }
    return {
      id: u.id,
      firstName: u.first_name ?? '',
      lastName: u.last_name ?? '',
      username: u.username ?? null,
      phone: null, // запрашивается отдельно через requestPhone()
    };
  }

  getInitData(): string {
    return this.sdk.initData;
  }

  async requestPhone(): Promise<string | null> {
    try {
      const result = await this.sdk.requestContact();
      return result?.phone ?? null;
    } catch {
      return null;
    }
  }

  showBackButton(): void {
    this.sdk.BackButton.show();
  }

  hideBackButton(): void {
    this.sdk.BackButton.hide();
  }

  onBackButtonClick(cb: () => void): void {
    this.sdk.BackButton.onClick(cb);
  }

  impactOccurred(style: 'light' | 'medium' | 'heavy'): void {
    this.sdk.HapticFeedback.impactOccurred(style).catch(() => {});
  }

  getTheme(): ThemeColors {
    return DEFAULT_THEME;
  }

  ready(): void {
    // window.WebApp не требует явного вызова ready()
  }

  close(): void {
    // Нет метода close() в документации MAX WebApp SDK
  }

  openLink(url: string): void {
    this.sdk.openLink(url);
  }

  openMaxLink(url: string): void {
    this.sdk.openMaxLink(url);
  }

  async share(params: ShareParams): Promise<'shared' | 'cancelled'> {
    const result = await this.sdk.share(params);
    return result.status;
  }

  enableClosingConfirmation(): void {
    this.sdk.enableClosingConfirmation();
  }

  disableClosingConfirmation(): void {
    this.sdk.disableClosingConfirmation();
  }
}
